import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { formatMoney, intervalLabel } from "@/lib/money";
import { SUBSCRIPTIONS_ENABLED } from "@/lib/featureFlags";

// Persistent (non-dismissible) conversion bar shown on EVERY page for logged-in
// free-tier users. Renders nothing for logged-out visitors or any paid/full tier.
// Also renders nothing when SUBSCRIPTIONS_ENABLED is off (no paid tier to nudge to).
export function UpgradeBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { entitlement, config, billingReady, startCheckout } = useBilling();
  const [busy, setBusy] = useState(false);

  if (!SUBSCRIPTIONS_ENABLED) return null;
  if (!user || !billingReady || entitlement?.tier !== "free") return null;
  // Staff (admins + team instructors) don't buy Pro — they manage the platform
  // and their teams. Hide the conversion nudge from them regardless of entitlement.
  if (user.role === "admin" || user.role === "instructor") return null;

  const price = config?.pro
    ? `${formatMoney(config.pro.price_cents, config.currency)} / ${intervalLabel(config.pro.interval)}`
    : null;

  const upgrade = async () => {
    setBusy(true);
    try {
      await startCheckout();
    } catch {
      toast.error("Could not start checkout. Please try again from the Pricing page.");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="upgrade-banner"
      className="no-print w-full bg-gradient-to-r from-[#0047AB] to-sky-500 text-white shadow-sm"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Lock className="w-3.5 h-3.5" />
          </span>
          <p className="text-sm leading-tight min-w-0 truncate">
            <span className="font-bold">You&apos;re on the Free plan.</span>
            <span className="hidden sm:inline text-white/90"> Upgrade to Pro to unlock every clinical simulator.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate("/pricing")}
            data-testid="upgrade-banner-plans-link"
            className="hidden md:inline text-sm font-semibold text-white/90 hover:text-white hover:underline whitespace-nowrap"
          >
            See plans
          </button>
          <Button
            onClick={upgrade}
            disabled={busy}
            data-testid="upgrade-banner-upgrade-btn"
            size="sm"
            className="bg-white text-[#0047AB] hover:bg-white/90 font-semibold h-8 px-3.5"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            <span className="whitespace-nowrap">Upgrade{price ? ` · ${price}` : " to Pro"}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
